import { useState } from 'react';
import {
	GetPolicyDetailsAPIResponse,
	PolicyDetailsMember,
	getPolicyDetails,
} from '@shory-health-individual/customer-api';
import { useAppPlatformContextOptions } from '../../../contexts/app';
import useDownloadFile from '../../useDownloadFile';

export type PolicyDetailsMemberEnriched = {
	gender?: string;
	relationship?: string;
	nationality?: string;
	firstName?: string;
	lastName?: string;
} & PolicyDetailsMember;

export type PolicyDetailsEnriched = GetPolicyDetailsAPIResponse & {
	members: PolicyDetailsMemberEnriched[];
	insuranceCompany?: {
		logoUrl?: string;
		shortName?: string;
	};
	plan?: string;
};

type Props = {
	policyDetails: PolicyDetailsEnriched;
	encryptedJourneyId: string;
	onViewPdf: (pdfUrl: string) => void;
	showNotReadyWarning: () => void;
	isWebView: boolean;
};

const usePolicyIssuance = (props: Props) => {
	const { policyDetails, encryptedJourneyId, onViewPdf, showNotReadyWarning, isWebView } = props;
	const { apiConnectionOptions } = useAppPlatformContextOptions();

	const [isCollapsibleOpen, setCollapsibleOpen] = useState(false);
	const [policyInformation, setPolicyInformation] = useState<GetPolicyDetailsAPIResponse>(policyDetails);

	const { onDownload } = useDownloadFile({ isWebView: isWebView });

	const onDownloadNetworkList = async () =>
		await onDownload({
			url: '/Quote/networksearch/download',
			parameters: { planId: policyInformation.planId },
			method: 'POST',
		});

	const onDownloadAllMedicalDeclarations = async () =>
		await onDownload({
			url: `/Member/DownloadAllMedicalForms/${encryptedJourneyId}`,
			method: 'PATCH',
		});

	const fetchAndViewPdf = async (propertyName: keyof GetPolicyDetailsAPIResponse) => {
		let policyDetails = policyInformation;

		if (!policyDetails[propertyName]) {
			policyDetails = await getPolicyDetails(apiConnectionOptions)({
				parameters: { encryptedJourneyId },
			});
			setPolicyInformation(policyDetails);
		}

		if (policyDetails[propertyName]) {
			onViewPdf(policyDetails[propertyName] as string);
		} else {
			showNotReadyWarning();
		}
	};

	return {
		isCollapsibleOpen,
		setCollapsibleOpen,
		fetchAndViewPdf,
		onDownloadNetworkList,
		onDownloadAllMedicalDeclarations,
	};
};

export default usePolicyIssuance;
